import { Icon, SkeletonLoader } from '@toggle/design-system';
import { Alert, formatTicker } from '@toggle/toggle';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AssetLogoContainer } from '~/components/asset-logo-container/AssetLogoContainer';
import { useGetInsightWidgetData } from '~/services/insights/insights-service';
import { getCardDate } from '~/utils/card-utils/card-utils';

import sharedStyles from '../../AlertsCard.module.scss';
import styles from './AlertsInsightCard.module.scss';

interface AlertsInsightCardProps {
  alertEvent: Partial<Alert> & Pick<Alert, 'source_id'> & { relation?: string };
  setIsDropdownOpen?: (isDropdownOpen: boolean) => void;
  variant?: 'toast' | 'card';
  dropdown?: React.ReactNode;
}

export const AlertsInsightCard = ({
  alertEvent,
  dropdown,
  variant = 'card',
}: AlertsInsightCardProps) => {
  const { t } = useTranslation(['alerts', 'whatsNew'], {
    useSuspense: false,
  });
  const {
    created_at: createdAt,
    source_id: sourceId,
    title,
    read = false,
    tracking_status: trackingStatus,
    metadata,
    relation,
  } = alertEvent;
  const isToastVariant = variant === 'toast';

  const { data, isLoading } = useGetInsightWidgetData(sourceId);
  const entity = data?.data?.entity;

  const isTrackingActive = trackingStatus === 'active';
  const isTrackingCompleted = trackingStatus === 'completed';

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '60px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!entity) {
    return null;
  }

  return (
    <>
      <div className={sharedStyles.header}>
        <div className={sharedStyles.headerLeft}>
          <div className={clsx(styles.assetNameText, { [styles.read]: read })}>
            {entity.name}
          </div>
          <div className={sharedStyles.dot} />
          <div className={styles.secondaryAssetInfoText}>
            {formatTicker(entity)}
          </div>
          {relation && (
            <>
              <div className={sharedStyles.dot} />
              <div className={styles.secondaryAssetInfoText}>{relation}</div>
            </>
          )}
        </div>
        {!isToastVariant && createdAt && (
          <div className={sharedStyles.headerRight}>
            <span className={sharedStyles.dateSpan}>
              {getCardDate(new Date(createdAt))}
            </span>
            {dropdown}
          </div>
        )}
      </div>
      <div className={sharedStyles.bodyWrapper}>
        <div className={sharedStyles.logoWrapper}>
          <AssetLogoContainer
            entity={entity}
            size={32}
            shouldUseBorderOnFallback
          />
          <div className={styles.logoBadge}>
            <Icon
              iconName="ExploreNav"
              size={12}
              fillColor="var(--icon-decorative-indigo)"
            />
          </div>
        </div>
        <div className={sharedStyles.textContent}>
          <p
            className={clsx(sharedStyles.primaryText, {
              [sharedStyles.read]: read,
              [sharedStyles.isToastVariant]: isToastVariant,
            })}
          >
            {title}
          </p>
          {!isToastVariant &&
            (trackingStatus ||
              metadata?.tracking_days_remaining !== undefined ||
              metadata?.tracking_days_elapsed !== undefined) && (
              <div className={styles.secondaryText}>
                {trackingStatus && (
                  <span
                    className={clsx(styles.trackingStatus, {
                      [styles.isTracking]: isTrackingActive,
                    })}
                  >
                    •{' '}
                    {t(
                      `alerts:alfredInsightCard.trackingStatus.${trackingStatus}`
                    )}
                  </span>
                )}
                {isTrackingCompleted &&
                  metadata?.tracking_days_elapsed !== undefined && (
                    <span className={styles.horizon}>
                      ·{' '}
                      {t('alerts:alfredInsightCard.daysElapsed', {
                        count: metadata.tracking_days_elapsed,
                      })}
                    </span>
                  )}
                {isTrackingActive &&
                  metadata?.tracking_days_remaining !== undefined && (
                    <span className={styles.horizon}>
                      ·{' '}
                      {t('alerts:alfredInsightCard.daysRemaining', {
                        count: metadata.tracking_days_remaining,
                      })}
                    </span>
                  )}
              </div>
            )}
        </div>
      </div>
    </>
  );
};
